import { VerticalFlexbox } from "@src/components/layout/Flexbox";
import { Button } from "@src/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@src/components/ui/command";
import { Label } from "@src/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@src/components/ui/popover";
import { cn } from "@src/lib/utils";
import { useField } from "formik";
import { Check, ChevronsUpDown } from "lucide-react";
import { useId, useState } from "react";
import {
  STATE_ITEMS,
  US_STATE_ABBR_TO_NAME,
  USState,
  type StateItem,
} from "./usStates";

export interface FormikStateSelectProps {
  name: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  containerClassName?: string;
  disabled?: boolean;
}

const KNOWN_ABBREVIATIONS = new Set<string>(
  STATE_ITEMS.map((item) => item.abbreviation),
);

function isUSState(value: string): value is USState {
  return KNOWN_ABBREVIATIONS.has(value);
}

/**
 * Formik-integrated, searchable US state select. Stores the selected state's
 * 2-letter abbreviation (`USState`) in Formik. Supports searching by either
 * the abbreviation or the state name.
 */
export const FormikStateSelect = ({
  name,
  label,
  placeholder = "Select state",
  required,
  containerClassName,
  disabled,
}: FormikStateSelectProps) => {
  const [field, meta, helpers] = useField<USState | "" | null | undefined>(
    name,
  );
  const id = useId();
  const error = meta.touched && meta.error ? String(meta.error) : undefined;
  const [open, setOpen] = useState(false);

  const selectedAbbreviation =
    typeof field.value === "string" && isUSState(field.value)
      ? field.value
      : undefined;
  const selectedItem: StateItem | undefined = selectedAbbreviation
    ? {
        abbreviation: selectedAbbreviation,
        menuItemLabel: `${selectedAbbreviation} - ${US_STATE_ABBR_TO_NAME[selectedAbbreviation]}`,
      }
    : undefined;

  return (
    <VerticalFlexbox gap="xs" className={cn("w-full", containerClassName)}>
      {label ? (
        <Label htmlFor={id} className="text-sm text-foreground">
          {label}
          {required ? (
            <span className="ml-0.5 text-destructive" aria-hidden>
              *
            </span>
          ) : null}
        </Label>
      ) : null}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-invalid={error ? "true" : undefined}
            disabled={disabled}
            onBlur={() => helpers.setTouched(true)}
            className={cn(
              "w-full justify-between font-normal",
              !selectedItem && "text-muted-foreground",
              error && "border-destructive ring-destructive",
            )}
          >
            <span className="truncate">
              {selectedItem ? selectedItem.menuItemLabel : placeholder}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[--radix-popover-trigger-width] p-0"
          align="start"
        >
          <Command>
            <CommandInput placeholder="Search state..." />
            <CommandList>
              <CommandEmpty>No state found.</CommandEmpty>
              <CommandGroup>
                {STATE_ITEMS.map((item) => (
                  <CommandItem
                    key={item.abbreviation}
                    value={item.menuItemLabel}
                    onSelect={() => {
                      helpers.setValue(item.abbreviation);
                      helpers.setTouched(true);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedAbbreviation === item.abbreviation
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                    {item.menuItemLabel}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {error ? <span className="text-xs text-destructive">{error}</span> : null}
    </VerticalFlexbox>
  );
};
